// noinspection RequiredAttributes

import React, {memo, useCallback, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";

import FroalaEditor from "froala-editor";
import "froala-editor/css/froala_editor.pkgd.min.css";
import "froala-editor/css/plugins/colors.min.css";
import "froala-editor/css/plugins/table.min.css";
import "froala-editor/css/plugins/code_view.min.css";
import "froala-editor/css/plugins/draggable.min.css";
import "froala-editor/css/plugins/char_counter.min.css";
import "froala-editor/css/plugins/image.min.css";
import "froala-editor/js/plugins/colors.min.js";
import "froala-editor/js/froala_editor.pkgd.min.js";
import "froala-editor/js/plugins/table.min.js";
import "froala-editor/js/plugins/draggable.min.js";
import "froala-editor/js/plugins/code_view.min.js";
import "froala-editor/js/plugins/font_family.min.js";
import "froala-editor/js/plugins/font_size.min.js";
import "froala-editor/js/plugins/lists.min.js";
import "froala-editor/js/plugins/align.min.js";
import "froala-editor/js/plugins/link.min.js";
import "froala-editor/js/plugins/paragraph_format.min.js";
import "froala-editor/js/plugins/char_counter.min.js";
import "froala-editor/js/plugins/image.min.js";
import "./TextEditor.Style.scss";
import {GlobalInputDelay, isHTML} from "../../Helpers";
import {useEventListener} from "../../Hooks";
import {ResizeIcon} from "../../Assets/icons";

// component to control or change textEditor from single location
export const TextEditorComponent = memo(
  ({
    wrapperClasses,
    labelClasses,
    isDisabled,
    labelValue,
    idRef,
    editorValue,
    height,
    width,
    isSubmitted,
    helperText,
    onEditorChange,
    onBlurChange,
    onEditorDelayedChange,
    parentTranslationPath,
    translationPath,
    onInit,
    tabIndex,
    placeholder,
  }) => {
    const {t} = useTranslation(parentTranslationPath);
    const [localValue, setLocalValue] = useState(editorValue);
    const [isBlurred, setIsBlurred] = useState(false);
    const [intValueIsNormalString, setIntValueIsNormalString] = useState(false);
    const timerRef = useRef(null);
    const editorRef = useRef(null);
    const isMountedRef = useRef(null);
    const resizeElementRef = useRef(null);
    const editorContainerRef = useRef(null);
    const [editorState, setEditorState] = useState(null);

    /**
     * @author Aladdin Al-awadat (a.alawadat@elevatus.io)
     * @Description this is to update the local value if parent value is updated &
     * not same as local with attention for delay if active or not
     */
    useEffect(() => {
      if (localValue !== editorValue && !timerRef.current) {
        setLocalValue(editorValue);
        if (editorRef.current) {
          editorRef.current.html.set(editorValue);
          editorRef.current.undo.saveStep();
        }
      }
    }, [editorValue, localValue]);

    /**
     * @author Aladdin Al-awadat (a.alawadat@elevatus.io)
     * @Description this is to prevent any memory leak if the user destroy
     * the component before setTimeout finished
     * @note destroy is happening when tap or route or refresh happening
     */
    useEffect(
      () => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
      },
      []
    );
    const onInitHandler = useCallback(() => {
      const editorElement = document.getElementById(idRef);
      if (
        !!editorElement &&
        !editorRef.current &&
        !editorState &&
        !isMountedRef.current
      ) {
        isMountedRef.current = true;
        const styles  = window.getComputedStyle(editorContainerRef.current);
        const bodyStyles=`body{
         font-family: ${styles["font-family"]};
         color: ${styles["color"]}; 
         margin-inline: 1rem;
         padding: 0;
        }`
        const editorLocale = new FroalaEditor(
          editorElement,
          {
            key: process.env.REACT_APP_FROALA_EDITOR_KEY,
            iframeStyle: bodyStyles,
            wordCounter: true,
            wordCounterLabel: "words",
            wordCounterBbCode: false,
            wordCounterTimeout: 0,
            useClasses: false,
            dragInline: true,
            toolbarButtons: {
              moreText: {
                buttonsVisible: 2,
                buttons: [
                  "fontSize",
                  "paragraphFormat",
                  "bold",
                  "italic",
                  "underline",
                  "strikeThrough",
                  "subscript",
                  "superscript",
                  "textColor",
                  "backgroundColor",
                  "inlineClass",
                  "inlineStyle",
                  "clearFormatting",
                ],
              },
              fontSizeDefaultSelection: 12,
              moreParagraph: {
                buttonsVisible: 0,
                buttons: [
                  "alignLeft",
                  "alignCenter",
                  "alignRight",
                  "alignJustify",
                  "formatOL",
                  "formatUL",
                  "paragraphStyle",
                  "lineHeight",
                  "outdent",
                  "indent",
                  "quote",
                ],
              },

              moreRich: {
                buttons: ["insertLink", "insertTable", "insertImage"],
                buttonsVisible: 0,
              },

              moreMisc: {
                buttons: ["html"],

                align: "right",

                buttonsVisible: 2,
              },
            },
            iframe: true,
            attribution: false,
            placeholderText:
              (parentTranslationPath &&
                placeholder &&
                t(`${translationPath}${placeholder}`)) ||
              placeholder ||
              null,
            width: width || "100%",
            tabIndex,
            fontFamilySelection: true,
            fontSizeSelection: true,
            fontSizeDefaultSelection: "11pt",
            fontSize: [
              "8",
              "9",
              "10",
              "11",
              "12",
              "13",
              "14",
              "15",
              "16",
              "17",
              "18",
              "19",
              "20",
              "22",
              "24",
              "26",
              "28",
              "30",
              "32",
              "34",
              "36",
              "48",
              "60",
              "72",
              "96",
            ],
            linkList: [],
            htmlRemoveTags: [],
            fontSizeUnit: "pt",
            listAdvancedTypes: true,
            charCounterCount: true,
            height,
            events: {
              "image.beforeUpload": function (files) {
                var localeEditor = this;
                if (files.length) {
                  var reader = new FileReader();
                  reader.onload = function (e) {
                    var result = e.target.result;
                    localeEditor.image.insert(
                      result,
                      null,
                      null,
                      localeEditor.image.get()
                    );
                  };
                  // Read image as base64.
                  reader.readAsDataURL(files[0]);
                }
                localeEditor.popups.hideAll();

                // Stop default upload chain.
                return false;
              },
              blur: function () {
                const innerHTML = this.html.get();
                if (this.codeView.isActive()) this.codeView.toggle();
                if (!isBlurred) setIsBlurred(true);
                if (timerRef.current) {
                  clearTimeout(timerRef.current);
                  timerRef.current = null;
                  onEditorDelayedChange(innerHTML);
                }
                if (onBlurChange) onBlurChange(innerHTML);
              },
              contentChanged: function () {
                const localContent = this.html.get();
                if (onEditorChange || onEditorDelayedChange)
                  setLocalValue(localContent);
                if (
                  editorValue &&
                  !isHTML(editorValue) &&
                  !intValueIsNormalString
                ) {
                  setIntValueIsNormalString(true);

                  return;
                }
                if (onEditorChange) onEditorChange(localContent);
                if (onEditorDelayedChange) {
                  if (timerRef.current) clearTimeout(timerRef.current);
                  // reduce the cpu usage if user type fast
                  timerRef.current = setTimeout(() => {
                    timerRef.current = null;
                    onEditorDelayedChange(localContent);
                  }, GlobalInputDelay);
                }
              },
            },
          },
          function () {
            // Call the method inside the initialized event.
            editorRef.current = editorLocale;
            if (isDisabled) editorLocale.edit.off();
            if (editorValue) {
              editorLocale.html.set(editorValue);
              setLocalValue(editorValue);
            } else editorLocale.html.set("");
            const iframeDocument = editorLocale.iframe_document;
            // const links = document.head.querySelectorAll(
            //   'link[rel="stylesheet"][href$=".chunk.css"], link[rel="stylesheet"][href*="main"][href*=".css"]'
            // );
            // if (links && links.length > 0) {
            //   links.forEach((link) => {
            //     iframeDocument.head.appendChild(link.cloneNode(true));
            //   });
            //
            //   return;
            // }
            // const arrStyleSheets = document.head.querySelectorAll(
            //   "style:not([data-s], .vjs-styles-defaults)"
            // );
            // for (const property in arrStyleSheets)
            //   if (arrStyleSheets[property].cloneNode)
            //     iframeDocument.head.appendChild(
            //       arrStyleSheets[property].cloneNode(true)
            //     );
            const styleElement = document.createElement("style");
            styleElement.textContent = `.container{
           max-width: 100%; padding:0 } body {color: initial !important;}
           html {overflow: auto !important;}`;
            iframeDocument.head.appendChild(styleElement);
            setEditorState(editorLocale);
            if (onInit) onInit(editorLocale);
          }
        );
      }
    }, [
      editorState,
      editorValue,
      height,
      idRef,
      intValueIsNormalString,
      isBlurred,
      isDisabled,
      onBlurChange,
      onEditorChange,
      onEditorDelayedChange,
      onInit,
      parentTranslationPath,
      placeholder,
      t,
      tabIndex,
      translationPath,
      width,
    ]);
    useEffect(() => {
      if (!isMountedRef.current) onInitHandler();
    }, [onInitHandler]);

    // destroy editor
    useEffect(
      () => () => {
        if (editorRef.current) {
          editorRef.current.destroy();
          editorRef.current = null;
          setEditorState(null);
          isMountedRef.current = false;
        }
      },
      []
    );

    // handle is disabled changes after initialization
    useEffect(() => {
      if (editorRef.current && editorState)
        if (isDisabled) editorRef.current.edit.off();
        else editorRef.current.edit.on();
    }, [isDisabled, editorState]);

    // handle place-holder changes after initialization
    useEffect(() => {
      if (editorRef.current && editorState)
        if (placeholder) {
          editorRef.current.opts.placeholderText =
            (parentTranslationPath &&
              placeholder &&
              t(`${translationPath}${placeholder}`)) ||
            placeholder ||
            null;

          if (!editorValue) {
            editorRef.current.html.set("");
            editorRef.current.placeholder.refresh();
          }
        }
    }, [
      isDisabled,
      editorState,
      placeholder,
      parentTranslationPath,
      t,
      translationPath,
      editorValue,
    ]);

    // handle is disabled changes after initialization
    useEffect(() => {
      if (editorRef.current && editorState)
        if (isDisabled) editorRef.current.edit.off();
        else editorRef.current.edit.on();
    }, [isDisabled, editorState]);

    const isResizingRef = useRef(false);
    const handleMouseMove = (e) => {
      if (isResizingRef.current) {
        const newHeight =
          e.clientY - editorContainerRef.current.getBoundingClientRect().top;
        const newEditorHeight =
          newHeight -
          document.querySelector(
            `#text-editor-wrapper${idRef} .fr-toolbar.fr-top`
          ).clientHeight -
          document.querySelector(
            `#text-editor-wrapper${idRef} .fr-second-toolbar`
          ).clientHeight -
          2;
        editorContainerRef.current.style.height = `${newHeight}px`;
        const wrapperElement = document.querySelector(
          `#text-editor-wrapper${idRef} .fr-wrapper`
        );
        wrapperElement.style.height = newEditorHeight + "px";
        editorRef.current.opts.height = newEditorHeight;
      }
    };
    const resizeStart = () => {
      isResizingRef.current = true;
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", () => {
        isResizingRef.current = false;
        document.removeEventListener("mousemove", handleMouseMove);
      });
    };
    useEventListener("mousedown", resizeStart, resizeElementRef.current);

    // noinspection LongLine,SpellCheckingInspection
    return (
      <div
        className={`text-editor-component-wrapper${
          (wrapperClasses && ` ${wrapperClasses}`) || ""
        }`}
      >
        {labelValue && (
          <div className="labels-wrapper">
            {labelValue && (
              <label
                htmlFor={idRef}
                className={`label-wrapper ${labelClasses}${
                  isDisabled ? " disabled" : ""
                }`}
              >
                {t(`${translationPath}${labelValue}`)}
              </label>
            )}
          </div>
        )}
        <div
          className="text-editor-wrapper position-relative"
          id={`text-editor-wrapper${idRef}`}
          ref={editorContainerRef}
        >
          <textarea id={idRef}></textarea>
          <div
            id={`resizable-handle${idRef}`}
            ref={resizeElementRef}
            className="resize-element"
          >
            <ResizeIcon />
          </div>
        </div>
        {helperText && (isSubmitted || isBlurred) && (
          <div className="error-wrapper">
            <span>{helperText}</span>
          </div>
        )}
      </div>
    );
  }
);

TextEditorComponent.displayName = "TextEditorComponent";

TextEditorComponent.propTypes = {
  wrapperClasses: PropTypes.string,
  labelClasses: PropTypes.string,
  isDisabled: PropTypes.bool,
  isSubmitted: PropTypes.bool,
  helperText: PropTypes.string,
  labelValue: PropTypes.string,
  placeholder: PropTypes.string,
  tabIndex: PropTypes.number,
  editorValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  toolbar: PropTypes.string,
  onEditorChange: PropTypes.func,
  onBlurChange: PropTypes.func,
  onInit: PropTypes.func,
  onEditorDelayedChange: PropTypes.func,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  idRef: PropTypes.string,
  rightToolbarExtend: PropTypes.string,
  leftToolbarExtend: PropTypes.string,
  fontFormats: PropTypes.string,
  imageTitle: PropTypes.bool,
  automaticUploads: PropTypes.bool,
  imageAdvTab: PropTypes.bool,
  imageCaption: PropTypes.bool,
  branding: PropTypes.bool,
  filePickerTypes: PropTypes.string,
  parentTranslationPath: PropTypes.string,
  translationPath: PropTypes.string,
  plugins: PropTypes.instanceOf(Array),
  menubar: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  isWithoutInsert: PropTypes.bool,
};

TextEditorComponent.defaultProps = {
  wrapperClasses: undefined,
  labelClasses: undefined,
  isDisabled: false,
  labelValue: undefined,
  editorValue: undefined,
  helperText: undefined,
  toolbar: undefined,
  onEditorChange: undefined,
  onEditorDelayedChange: undefined,
  onBlurChange: undefined,
  isSubmitted: undefined,
  height: 250,
  width: "100%",
  idRef: "TextEditorComponentRef",
  parentTranslationPath: undefined,
  translationPath: undefined,
  plugins: undefined,
  tabIndex: undefined,
  placeholder: undefined,
  isWithoutInsert: false,
  menubar: undefined,
  onInit: undefined,
  imageTitle: true,
  automaticUploads: true,
  imageAdvTab: true,
  imageCaption: true,
  branding: false,
  filePickerTypes: "image",
  rightToolbarExtend: "",
  leftToolbarExtend: "",
};
